import { ArrowLeft, LayoutDashboard, Lightbulb, RotateCcw, Send } from "lucide-react";
import { useMemo, useState } from "react";
import type { SelectedContext } from "../../services/storage";
import { CanvasProgress } from "./CanvasProgress";
import { CanvasTile } from "./CanvasTile";
import {
  BOX_COUNT,
  canSubmit,
  filledCount,
  isBusy,
  readyCount,
  type CanvasSource,
} from "./canvasModel";
import { DASHBOARD_TIP } from "./prompts";
import { SourceSelect } from "./SourcePicker";
import { useQueryableSources } from "./useQueryableSources";
import { DEFAULT_DASHBOARD_NAME, useDashboardCanvas } from "./useDashboardCanvas";

const MAX_NAME_CHARS = 160;

interface DashboardCanvasPageProps {
  selectedContext: SelectedContext | null;
  onSelectContext: (context: SelectedContext) => void;
  /** Back to the dashboards workspace, where the saved draft is listed. */
  onBack: () => void;
}

/**
 * A blank canvas of boxes, one question each. Submitting turns every question
 * into a governed chart from the chosen semantic model and saves them together
 * as a draft dashboard.
 */
export function DashboardCanvasPage({
  selectedContext,
  onSelectContext,
  onBack,
}: DashboardCanvasPageProps) {
  const { sources, error: sourceError, noSources, choose } = useQueryableSources(
    selectedContext,
    onSelectContext,
  );
  const [name, setName] = useState("");

  const source = useMemo<CanvasSource | null>(
    () =>
      selectedContext
        ? {
            contextId: selectedContext.contextId,
            contextVersion: selectedContext.contextVersion,
            name: selectedContext.name,
          }
        : null,
    [selectedContext],
  );
  const canvas = useDashboardCanvas(source, name);
  const { boxes, dashboard, submitted, submitting, notice } = canvas;

  const filled = filledCount(boxes);
  const ready = readyCount(boxes);
  const busy = submitting || boxes.some(isBusy);
  const submittable = !submitted && !!source && !noSources && canSubmit(boxes);

  return (
    <div className="content-stack dashboard-canvas">
      <header className="canvas-head">
        <button type="button" className="canvas-back" onClick={onBack}>
          <ArrowLeft size={15} /> Dashboards
        </button>
        <div className="canvas-head-fields">
          <label className="canvas-field canvas-name">
            <span>Dashboard name</span>
            <input
              value={name}
              maxLength={MAX_NAME_CHARS}
              placeholder={DEFAULT_DASHBOARD_NAME}
              disabled={!!dashboard}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <SourceSelect
            sources={sources}
            value={source?.contextId ?? ""}
            disabled={submitted}
            onChange={choose}
          />
        </div>
        {!submitted && (
          <div className="canvas-head-actions">
            <CanvasProgress
              value={filled}
              total={BOX_COUNT}
              label={`${filled} of ${BOX_COUNT} questions`}
            />
            <button
              type="button"
              className="button primary"
              disabled={!submittable}
              onClick={() => void canvas.submit()}
              title={submittable ? undefined : `Fill all ${BOX_COUNT} boxes to submit`}
            >
              <Send size={15} /> Submit
            </button>
          </div>
        )}
      </header>

      {sourceError && (
        <div className="form-error" role="alert">
          {sourceError}
        </div>
      )}
      {noSources && !sourceError && (
        <div className="inline-notice" role="status">
          No semantic model can be queried yet. Publish one before building a dashboard.
        </div>
      )}
      {notice && (
        <div className="form-error" role="alert">
          {notice}
        </div>
      )}

      <div className="canvas-grid">
        {boxes.map((box) => (
          <CanvasTile
            key={box.id}
            box={box}
            submitted={submitted}
            domain={selectedContext?.dataDomain ?? null}
            onQuestion={(question) => canvas.setQuestion(box.id, question)}
            onKind={(kind) => canvas.setKind(box.id, kind)}
            onAsk={() => void canvas.ask(box)}
            onRetry={() => void canvas.retry(box)}
            onEdit={() => canvas.edit(box)}
            onChooseOption={(option) => void canvas.chooseOption(box, option)}
            onKeepCandidate={(index) => void canvas.keepCandidate(box, index)}
          />
        ))}
      </div>
      {!submitted && (
        <p className="canvas-tip">
          <Lightbulb size={14} aria-hidden="true" /> {DASHBOARD_TIP}
        </p>
      )}

      {submitted && (
        <footer className="canvas-foot" aria-live="polite">
          <span>
            {dashboard
              ? `Saved as draft · ${ready} of ${BOX_COUNT} ready`
              : busy
                ? "Building your dashboard…"
                : `${ready} of ${BOX_COUNT} ready · nothing saved yet`}
          </span>
          <div className="canvas-foot-actions">
            <button
              type="button"
              className="button secondary small"
              onClick={canvas.startOver}
              disabled={busy}
            >
              <RotateCcw size={13} /> New canvas
            </button>
            {dashboard && (
              <button type="button" className="button primary small" onClick={onBack}>
                <LayoutDashboard size={13} /> Open in workspace
              </button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}
